import type React from 'react';
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { APP_NAME, resolveRouteTitle } from '@/lib/route-titles';

const FOCUS_TARGET_ID = 'route-focus-target';

/**
 * Creates the element focus lands on when the page has no heading to name it.
 *
 * Placed imperatively rather than rendered, because it has to sit at the start
 * of whichever `<main>` the current route uses. This component is mounted above
 * `<Routes>`, so a node rendered here would fall before `AppSidebar` in the DOM
 * and the next Tab would walk into the sidebar -- the very thing this component
 * exists to prevent. React never owns the node, so moving it between hosts
 * costs nothing; it is an extra child of `<main>`, which React reconciles
 * against its own nodes by reference and so leaves alone.
 *
 * Built fresh each time rather than reused: focus on a node that has never held
 * it always fires an event, which is what a screen reader announces, so two
 * fallback routes in a row are never silent.
 */
function createFocusTarget(label: string): HTMLElement {
  const target = document.createElement('div');
  target.id = FOCUS_TARGET_ID;
  target.className = 'sr-only';
  target.tabIndex = -1;
  target.textContent = label;

  const host = document.querySelector('main') ?? document.body;
  host.insertBefore(target, host.firstChild);
  return target;
}

function removeFocusTarget(): void {
  document.getElementById(FOCUS_TARGET_ID)?.remove();
}

/**
 * Stands the fallback down once the page renders the heading it was standing in
 * for, so the two never coexist.
 *
 * A detail page is still loading when we navigate to it, so the fallback names
 * the section; moments later the record arrives and the page renders its own
 * `<h1>`. Left alone, the fallback would sit above that heading for as long as
 * the page is open -- invisible, but read as a stray "Clients" ahead of
 * "BDO USA, LLP" by anyone moving through the page with a virtual cursor.
 *
 * The label is blanked rather than removed while it still holds focus: the user
 * may not have moved yet, and pulling the focused node out of the document
 * would drop them back to the top of the document and into the sidebar.
 */
function standDownWhenHeadingArrives(target: HTMLElement): MutationObserver | null {
  const host = target.parentElement;
  if (!host) return null;

  const observer = new MutationObserver(() => {
    const heading = document.querySelector<HTMLElement>('main h1');
    if (!heading?.textContent?.trim()) return;
    observer.disconnect();
    if (document.activeElement === target) {
      target.textContent = '';
    } else {
      target.remove();
    }
  });

  observer.observe(host, { childList: true, subtree: true, characterData: true });
  return observer;
}

/**
 * Moves focus to the top of the page after a client-side navigation, which is
 * also what announces the new page (WCAG 2.4.3 Focus Order).
 *
 * `PageTransition` swaps the page body without touching focus, so following a
 * sidebar link left a keyboard user still parked on that link, tabbing onward
 * through the rest of the sidebar, while a screen reader said nothing.
 * Updating `document.title` does not help -- assistive tech only reads a title
 * on a full page load, never on an in-app navigation.
 *
 * Focus goes to the page `<h1>`, not the `<main>` landmark. Focusing the
 * landmark works, but NVDA treats focus on a `tabindex="-1"` container as
 * "read this container" and recites the whole page: on `/payments` that was
 * both date pickers, four filters and eleven column headers before the user
 * could do anything. The heading names the page in one utterance.
 */
export const RouteAnnouncer: React.FC = () => {
  const { pathname } = useLocation();
  // Seeded with the pathname we arrived on, so neither the first mount nor
  // StrictMode's second pass steals focus from the page the user just loaded.
  const lastPathname = useRef(pathname);
  const watcher = useRef<MutationObserver | null>(null);

  useEffect(
    () => () => {
      watcher.current?.disconnect();
      removeFocusTarget();
    },
    []
  );

  useEffect(() => {
    if (lastPathname.current === pathname) return;
    lastPathname.current = pathname;

    // Whatever the last route left behind names that route, not this one, and
    // `<main>` outlives the pages inside it -- so one visit to a detail page
    // would otherwise leave a stray "Payments" sitting at the top of every page
    // that followed, for anyone reading with a virtual cursor.
    watcher.current?.disconnect();
    watcher.current = null;
    removeFocusTarget();

    // Radix keeps a closed dialog mounted through its exit animation with
    // `aria-modal` still set, and `nav-main` closes the mobile drawer as the
    // link is followed -- so match on `data-state` too, or every navigation
    // from that drawer would skip the focus move it most needs.
    if (document.querySelector('[aria-modal="true"][data-state="open"]')) return;

    // An empty heading is no better than a missing one: `/suppliers` renders its
    // <h1> from a config that has not loaded yet at this point, and focusing it
    // made NVDA say "blank".
    const heading = document.querySelector<HTMLElement>('main h1');
    if (heading?.textContent?.trim()) {
      // The page owns its heading and React does not manage `tabindex` on it,
      // so setting the attribute here sticks, and costs nothing on re-render.
      if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
      heading.focus();
      return;
    }

    // Every detail page while it loads, `/prospect-module`, and the
    // registration pages, which have no `<main>` either.
    const target = createFocusTarget(resolveRouteTitle(pathname) ?? APP_NAME);
    target.focus();
    watcher.current = standDownWhenHeadingArrives(target);
  }, [pathname]);

  return null;
};
